import { FocusEvent, KeyboardEvent, useCallback } from "react";
import { Editor, Point, Range } from "slate";
import isHotkey from "is-hotkey";

import { UseSlatePlugin } from "components/slate/types";
import { SlashMenuProps } from "components/slate/plugins/menus/slashMenu/types";
import useZustandCreateStore from "hooks/useZustandCreateStore";
import createState from "components/slate/plugins/menus/slashMenu/state";
import useMemoObject from "hooks/useMemoObject";
import { ELEMENTS_TEXT_BLOCKS } from "components/slate/plugins/menus/componentMenu/constants";
import getBeforeText from "components/slate/queries/getBeforeText";
import getAfterText from "components/slate/queries/getAfterText";
import { NavigationKeys } from "constants/navigationKeys";

const useSlashMenuPlugin: UseSlatePlugin<{}, SlashMenuProps> = () => {
  const { state, getState } = useZustandCreateStore(createState, []);

  const onChange = useCallback(
    (editor) => () => {
      const { target, setSearch, closeMenu } = getState();
      const { selection } = editor;

      if (target != null && selection && Range.isCollapsed(selection)) {
        const newTarget = {
          anchor: Range.start(target),
          focus: Range.start(selection),
        };

        const isForward =
          Range.isForward(newTarget) && !Range.isCollapsed(newTarget);
        const isSameBlock =
          newTarget.anchor.path[0] === newTarget.focus.path[0];

        if (isForward && isSameBlock) {
          const skipTrigger = Editor.after(editor, Range.start(target));
          const searchTarget = {
            anchor: skipTrigger,
            focus: Range.start(selection),
          };
          const search = Editor.string(editor, searchTarget);

          setSearch(search, newTarget);

          return;
        }

        closeMenu();
      }
    },
    [getState]
  );

  const onKeyDown = useCallback(
    (editor: Editor) => (e: KeyboardEvent) => {
      const { target, openMenu } = getState();

      const { selection } = editor;

      if (target == null) {
        if (!e.metaKey && e.key === "/" && selection) {
          // check if it is right block
          const blockEntry = Editor.above(editor, {
            mode: "lowest",
            match: (node) => {
              // @ts-ignore
              return (
                Editor.isBlock(editor, node) &&
                // @ts-ignore
                ELEMENTS_TEXT_BLOCKS.has(node.type)
              );
            },
          });

          // check if slash pressed not in inline
          const [inlinesMatch] = Editor.nodes(editor, {
            mode: "lowest",
            match: (node) => Editor.isInline(editor, node),
          });

          let isStart = false;
          let isEnd = false;
          if (blockEntry) {
            const blockRange = Editor.range(editor, blockEntry[1]);
            const [start, end] = Editor.edges(editor, selection);
            isStart =
              Point.equals(blockRange.anchor, start) ||
              getBeforeText(editor, start, { unit: "character" }) === " ";
            isEnd =
              Point.equals(blockRange.focus, end) ||
              getAfterText(editor, end, { unit: "character" }) === " ";
          }

          if (blockEntry && !inlinesMatch && isStart && isEnd) {
            // open slash menu on slash pressed
            openMenu(selection);
          }
        }
      } else {
        if (
          [NavigationKeys.ArrowRight, NavigationKeys.ArrowLeft].some((hotkey) =>
            isHotkey(hotkey, e)
          )
        ) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }
    },
    [getState]
  );

  const onBlur = useCallback(
    (editor: Editor) => (e: FocusEvent) => {
      const { closeMenu } = getState();
      closeMenu();
    },
    [getState]
  );

  return {
    handlers: {
      onKeyDown,
      onBlur,
    },
    onChange,
    state: useMemoObject<SlashMenuProps>({
      initialTarget: state.initialTarget,
      target: state.target,
      search: state.search,
      closeMenu: state.closeMenu,
    }),
  };
};

export default useSlashMenuPlugin;
